// ============================================================
// Report sections of the client portal — each reads one v2 report with
// the portal token (st = the selected client). Reuses the dashboard's
// report building blocks (Kpi, LineChartCard, BreakdownTable, …).
// ============================================================

import { useMemo, type ReactNode } from 'react'
import {
    Alert,
    Box,
    Chip,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material'

import {
    usePortalBreakdownQuery,
    usePortalEcommerceQuery,
    usePortalEventsQuery,
    usePortalGoalsQuery,
    usePortalOverviewQuery,
    usePortalPerformanceQuery,
    usePortalRealtimeQuery,
    usePortalRetentionQuery,
    usePortalTimeseriesQuery,
} from '@/apis/portalApi'
import { fillDailyGaps, mergeSeries, type V2RangeArgs } from '@/apis/v2Mappers'
import {
    BreakdownTable,
    Kpi,
    LineChartCard,
    ReportStatus,
    SectionCard,
    fmtMoney,
    fmtMs,
    fmtNum,
    fmtPct,
} from '@/modules/reports/common'
import { RatingChip, fmtVital } from '@/modules/reports/QualityPage'

import { withAlpha } from './portalTheme'

export interface SectionProps {
    args: V2RangeArgs
    interval: 'hour' | 'day' | 'week' | 'month'
    currency: string
    colors: string[]
}

function KpiGrid({ children }: { children: ReactNode }) {
    return (
        <Grid container spacing={2}>
            {children}
        </Grid>
    )
}

function Cell({ children }: { children: ReactNode }) {
    return <Grid size={{ xs: 6, md: 3 }}>{children}</Grid>
}

export function OverviewSection({ args, currency }: SectionProps) {
    const { data, isLoading, error } = usePortalOverviewQuery(args)
    const k = data?.kpis
    const ch = data?.change ?? {}
    const hasCommerce = !!k && ((k.purchases ?? 0) > 0 || (k.leads ?? 0) > 0)
    return (
        <ReportStatus isLoading={isLoading} error={error}>
            {data?.source === 'rollup' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    This range is served from daily totals, so session-level metrics are not
                    available.
                </Alert>
            )}
            {k && (
                <KpiGrid>
                    <Cell>
                        <Kpi title="Visitors" value={fmtNum(k.visitors)} change={ch.visitors} />
                    </Cell>
                    <Cell>
                        <Kpi title="Sessions" value={fmtNum(k.sessions)} change={ch.sessions} />
                    </Cell>
                    <Cell>
                        <Kpi title="Pageviews" value={fmtNum(k.pageviews)} change={ch.pageviews} />
                    </Cell>
                    <Cell>
                        <Kpi
                            title="Bounce rate"
                            value={fmtPct(k.bounce_rate)}
                            change={ch.bounce_rate}
                            invert
                        />
                    </Cell>
                    <Cell>
                        <Kpi
                            title="Avg. session"
                            value={fmtMs(k.avg_session_ms)}
                            change={ch.avg_session_ms}
                        />
                    </Cell>
                    <Cell>
                        <Kpi
                            title="Pages / session"
                            value={fmtNum(k.pages_per_session, 2)}
                            change={ch.pages_per_session}
                        />
                    </Cell>
                    {hasCommerce && (
                        <>
                            <Cell>
                                <Kpi
                                    title="Revenue"
                                    value={fmtMoney(k.revenue, currency)}
                                    change={ch.revenue}
                                />
                            </Cell>
                            <Cell>
                                <Kpi
                                    title="Purchases"
                                    value={fmtNum(k.purchases)}
                                    change={ch.purchases}
                                />
                            </Cell>
                            <Cell>
                                <Kpi
                                    title="Conversion rate"
                                    value={fmtPct(k.conversion_rate)}
                                    change={ch.conversion_rate}
                                />
                            </Cell>
                            <Cell>
                                <Kpi title="Leads" value={fmtNum(k.leads)} change={ch.leads} />
                            </Cell>
                        </>
                    )}
                </KpiGrid>
            )}
        </ReportStatus>
    )
}

export function TimeseriesSection({ args, interval, colors }: SectionProps) {
    const pageviews = usePortalTimeseriesQuery({ ...args, metric: 'pageviews', interval })
    const visitors = usePortalTimeseriesQuery({ ...args, metric: 'visitors', interval })
    const rows = useMemo(
        () =>
            fillDailyGaps(
                mergeSeries({ visitors: visitors.data, pageviews: pageviews.data }),
                interval,
                args.from,
                args.to,
                ['visitors', 'pageviews'],
            ),
        [visitors.data, pageviews.data, interval, args.from, args.to],
    )
    return (
        <ReportStatus
            isLoading={pageviews.isLoading || visitors.isLoading}
            error={pageviews.error ?? visitors.error}
        >
            <LineChartCard
                title="Traffic over time"
                rows={rows}
                interval={interval}
                colors={colors}
                series={[
                    { key: 'visitors', label: 'Visitors' },
                    { key: 'pageviews', label: 'Pageviews' },
                ]}
            />
        </ReportStatus>
    )
}

function BreakdownCard({
    args,
    title,
    dim,
    event,
    keyLabel,
    countLabel,
}: {
    args: V2RangeArgs
    title: string
    dim: string
    event?: string
    keyLabel: string
    countLabel?: string
}) {
    const {
        data = [],
        isLoading,
        error,
    } = usePortalBreakdownQuery({ ...args, dim, event, limit: 10 })
    return (
        <SectionCard title={title}>
            <ReportStatus
                isLoading={isLoading}
                error={error}
                isEmpty={data.length === 0}
                emptyTitle="No data for this range"
            >
                <BreakdownTable rows={data} keyLabel={keyLabel} countLabel={countLabel} />
            </ReportStatus>
        </SectionCard>
    )
}

export function BreakdownSection({ args }: SectionProps) {
    return (
        <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
                <BreakdownCard args={args} title="Channels" dim="channel" keyLabel="Channel" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <BreakdownCard
                    args={args}
                    title="Top pages"
                    dim="path"
                    event="page_view"
                    keyLabel="Page"
                    countLabel="Views"
                />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <BreakdownCard args={args} title="Countries" dim="country" keyLabel="Country" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <BreakdownCard args={args} title="Devices" dim="device" keyLabel="Device" />
            </Grid>
        </Grid>
    )
}

export function EventsSection({ args }: SectionProps) {
    const { data = [], isLoading, error } = usePortalEventsQuery(args)
    return (
        <SectionCard title="Events">
            <ReportStatus isLoading={isLoading} error={error} isEmpty={data.length === 0}>
                <BreakdownTable rows={data} keyLabel="Event" showValue maxRows={25} />
            </ReportStatus>
        </SectionCard>
    )
}

export function EcommerceSection({ args, currency }: SectionProps) {
    const { data, isLoading, error } = usePortalEcommerceQuery({ ...args, limit: 10 })
    const empty = !!data && !data.purchases && data.funnel.every(s => !s.events)
    return (
        <SectionCard title="Ecommerce">
            <ReportStatus
                isLoading={isLoading}
                error={error}
                isEmpty={empty}
                emptyTitle="No ecommerce activity in this range"
            >
                {data && (
                    <>
                        <KpiGrid>
                            <Cell>
                                <Kpi title="Revenue" value={fmtMoney(data.revenue, currency)} />
                            </Cell>
                            <Cell>
                                <Kpi title="Purchases" value={fmtNum(data.purchases)} />
                            </Cell>
                            <Cell>
                                <Kpi
                                    title="Avg. order value"
                                    value={fmtMoney(data.aov, currency)}
                                />
                            </Cell>
                            <Cell>
                                <Kpi title="Conversion rate" value={fmtPct(data.conversion_rate)} />
                            </Cell>
                        </KpiGrid>
                        {data.items.length > 0 && (
                            <Table size="small" sx={{ mt: 2 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Item</TableCell>
                                        <TableCell align="right">Views</TableCell>
                                        <TableCell align="right">Add to carts</TableCell>
                                        <TableCell align="right">Qty purchased</TableCell>
                                        <TableCell align="right">Revenue</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.items.map(it => (
                                        <TableRow key={it.item_id} hover>
                                            <TableCell>{it.item_id}</TableCell>
                                            <TableCell align="right">{fmtNum(it.views)}</TableCell>
                                            <TableCell align="right">
                                                {fmtNum(it.add_to_carts)}
                                            </TableCell>
                                            <TableCell align="right">
                                                {fmtNum(it.purchased_qty)}
                                            </TableCell>
                                            <TableCell align="right">
                                                {fmtMoney(it.revenue, currency)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </>
                )}
            </ReportStatus>
        </SectionCard>
    )
}

export function GoalsSection({ args, currency }: SectionProps) {
    const { data, isLoading, error } = usePortalGoalsQuery(args)
    const goals = data?.goals ?? []
    return (
        <SectionCard title="Goals">
            <ReportStatus
                isLoading={isLoading}
                error={error}
                isEmpty={goals.length === 0}
                emptyTitle="No goals set up yet"
            >
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Goal</TableCell>
                            <TableCell align="right">Conversions</TableCell>
                            <TableCell align="right">Conversion rate</TableCell>
                            <TableCell align="right">Value</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {goals.map(g => (
                            <TableRow key={g.id} hover>
                                <TableCell>{g.name}</TableCell>
                                <TableCell align="right">{fmtNum(g.conversions)}</TableCell>
                                <TableCell align="right">{fmtPct(g.conversion_rate)}</TableCell>
                                <TableCell align="right">{fmtMoney(g.value, currency)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </ReportStatus>
        </SectionCard>
    )
}

const REALTIME_POLL_MS = 30_000

export function RealtimeSection({ args }: SectionProps) {
    const { data, isLoading, error } = usePortalRealtimeQuery(
        { projectId: args.projectId, subTenant: args.subTenant ?? null, minutes: 30 },
        { pollingInterval: REALTIME_POLL_MS, skipPollingIfUnfocused: true },
    )
    return (
        <SectionCard
            title="Right now"
            action={<Chip size="small" color="success" label="Last 30 minutes" />}
        >
            <ReportStatus isLoading={isLoading} error={error}>
                {data && (
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box sx={{ display: 'grid', gap: 2 }}>
                                <Kpi title="Active visitors" value={fmtNum(data.active_visitors)} />
                                <Kpi title="Active sessions" value={fmtNum(data.active_sessions)} />
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, md: 8 }}>
                            {data.top_pages.length === 0 ? (
                                <Typography color="text.secondary" variant="body2">
                                    No one is on the site right now.
                                </Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Page</TableCell>
                                            <TableCell align="right">Sessions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.top_pages.slice(0, 10).map(p => (
                                            <TableRow key={p.path}>
                                                <TableCell>{p.path}</TableCell>
                                                <TableCell align="right">
                                                    {fmtNum(p.sessions)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Grid>
                    </Grid>
                )}
            </ReportStatus>
        </SectionCard>
    )
}

export function RetentionSection({ args, colors }: SectionProps) {
    const { data, isLoading, error } = usePortalRetentionQuery({
        ...args,
        interval: 'week',
        periods: 8,
    })
    return (
        <SectionCard title="Returning visitors (weekly cohorts)">
            <ReportStatus
                isLoading={isLoading}
                error={error}
                isEmpty={!!data && data.cohorts.length === 0}
                emptyTitle="Not enough returning visitors yet"
            >
                {data && (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Week of</TableCell>
                                    <TableCell align="right">Visitors</TableCell>
                                    {Array.from({ length: data.periods }, (_, i) => (
                                        <TableCell key={i} align="center">
                                            W{i}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.cohorts.map(c => (
                                    <TableRow key={c.cohort}>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {c.cohort}
                                        </TableCell>
                                        <TableCell align="right">{fmtNum(c.size)}</TableCell>
                                        {c.rates.map((r, i) => (
                                            <TableCell
                                                key={i}
                                                align="center"
                                                sx={{
                                                    fontSize: 12,
                                                    bgcolor: r
                                                        ? withAlpha(colors[0], 0.1 + r * 0.9)
                                                        : undefined,
                                                    color: (r ?? 0) > 0.5 ? '#fff' : undefined,
                                                }}
                                            >
                                                {r ? fmtPct(r) : ''}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </ReportStatus>
        </SectionCard>
    )
}

export function PerformanceSection({ args }: SectionProps) {
    const { data, isLoading, error } = usePortalPerformanceQuery(args)
    const metrics = data?.metrics ?? []
    return (
        <SectionCard title="Page speed (Core Web Vitals)">
            <ReportStatus
                isLoading={isLoading}
                error={error}
                isEmpty={metrics.length === 0}
                emptyTitle="No page-speed data in this range"
            >
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Metric</TableCell>
                            <TableCell align="right">75th percentile</TableCell>
                            <TableCell align="right">Samples</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {metrics.map(m => (
                            <TableRow key={m.metric}>
                                <TableCell>{m.metric}</TableCell>
                                <TableCell align="right">{fmtVital(m.metric, m.p75)}</TableCell>
                                <TableCell align="right">{fmtNum(m.samples)}</TableCell>
                                <TableCell align="right">
                                    <RatingChip rating={m.rating} />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </ReportStatus>
        </SectionCard>
    )
}
